import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Fire, ArrowRight } from "@phosphor-icons/react";
import api from "../lib/api";

// Most-Watched-This-Week rail. Drops onto the Home page above the
// devotional / continue-watching rails. Pulls trending items across
// every media bucket (movies, music_videos, sermons, kids, comedy,
// etc.) and resolves each to a deep link inside the app.
//
// We deliberately keep the card markup minimal — the goal is fast
// social proof, not a full media browser. Each card opens its
// canonical page inside the relevant bucket, where the existing
// InAppVideoPlayer / InAppMoviePlayer handles autoplay + queue.

// Map a backend bucket key to its canonical front-end route. Used by
// the rail to deep-link a card to the page where the player auto-
// opens for that item.
const BUCKET_ROUTES = {
  movies:           "/movies",
  videos:           "/videos",
  music_videos:     "/music-videos",
  christian_rap:    "/christian-rap",
  comedy:           "/comedy",
  sermons:          "/sermons",
  lessons:          "/lessons",
  choir:            "/choir",
  gospel_music:     "/gospel-music",
  kids:             "/kids",
  community_shorts: "/community-shorts",
};

const BUCKET_LABEL = {
  movies:           "Film",
  videos:           "Story",
  music_videos:     "Music Video",
  christian_rap:    "Faith-Based Rap",
  comedy:           "Comedy",
  sermons:          "Sermon",
  lessons:          "Lesson",
  choir:            "Choir",
  gospel_music:     "Gospel",
  kids:             "Kids",
  community_shorts: "Short",
};

function formatViews(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  return String(n);
}

export default function MostWatchedRail() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api
      .get("/analytics/most-watched", { params: { limit: 12, days: 7 } })
      .then(({ data }) => setItems(data?.items || []))
      .catch(() => setItems([]));
  }, []);

  // Hide entirely if data hasn't loaded — avoids flashing an empty
  // rail when the network is slow. The Home page falls back to the
  // existing devotional + continue-watching above us gracefully.
  if (!items || items.length === 0) return null;

  return (
    <section className="mt-10 px-4 sm:px-6 lg:px-10" data-testid="most-watched-rail">
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-center gap-2">
          <Fire size={18} weight="fill" className="text-terracotta" />
          <h2 className="font-heading text-xl text-forest font-medium">
            Most Watched This Week
          </h2>
        </div>
        <span className="text-[10px] tracking-[0.18em] uppercase text-muted_ink font-bold">
          7d trending
        </span>
      </div>
      <div className="-mx-4 sm:mx-0 overflow-x-auto pb-2 hide-scrollbar">
        <ul className="flex gap-3 px-4 sm:px-0 sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-3">
          {items.slice(0, 12).map((it, idx) => {
            const route = BUCKET_ROUTES[it.bucket] || "/discover";
            const label = BUCKET_LABEL[it.bucket] || it.bucket || "Trending";
            const href = `${route}?play=${encodeURIComponent(it.item_id)}`;
            // Touch-start prefetch — saves ~150–250ms of perceived
            // latency on mobile because the destination page begins
            // loading the moment the user touches, not when they lift.
            const prefetch = () => {
              try {
                const id = `prefetch-mw-${it.bucket}-${it.item_id}`;
                if (document.getElementById(id)) return;
                const link = document.createElement("link");
                link.id = id;
                link.rel = "prefetch";
                link.href = href;
                document.head.appendChild(link);
              } catch (_) { /* noop */ }
            };
            return (
              <li key={`${it.bucket}-${it.item_id}`} className="shrink-0 w-[230px] sm:w-auto">
                <Link
                  to={href}
                  onTouchStart={prefetch}
                  onMouseEnter={prefetch}
                  data-testid={`most-watched-${idx}`}
                  className="block rounded-2xl border-2 border-forest/15 bg-cream hover:border-terracotta hover:bg-white transition-all p-4 h-full"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[9px] tracking-[0.22em] uppercase font-bold text-terracotta">
                      #{idx + 1}
                    </span>
                    <span className="text-[9px] tracking-[0.18em] uppercase text-muted_ink font-bold">
                      {label}
                    </span>
                  </div>
                  <p className="text-sm font-semibold text-ink line-clamp-2 capitalize">
                    {(it.item_id || "")
                      .replace(/[-_]/g, " ")
                      .replace(/\d+$/, "")
                      .trim() || "Trending"}
                  </p>
                  <div className="mt-3 flex items-center justify-between text-[11px] text-muted_ink">
                    <span className="inline-flex items-center gap-1">
                      <Fire size={11} weight="fill" className="text-terracotta" />
                      <b className="text-ink">{formatViews(it.views)}</b> views
                    </span>
                    <ArrowRight size={11} weight="bold" className="text-forest" />
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
